/**
 * RepFilterPanel
 * Sidebar with checkboxes per active rep, select all/none, visibility toggles
 */

import React from "react";
import type { SalesPosition } from "../../types/sales.types";
import { POSITION_TYPE_LABELS } from "../../types/sales.types";
import {
  getRepColor,
  EVENT_TYPE_COLORS,
  EVENT_TYPE_LABELS,
  AVAILABILITY_TYPE_COLORS,
  AVAILABILITY_TYPE_LABELS,
} from "../../types/salesCalendar.types";

interface RepFilterPanelProps {
  positions: SalesPosition[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  showEvents: boolean;
  showAvailability: boolean;
  showCalendarRequests: boolean;
  onToggleEvents: () => void;
  onToggleAvailability: () => void;
  onToggleCalendarRequests: () => void;
}

const RepFilterPanel: React.FC<RepFilterPanelProps> = ({
  positions,
  selectedIds,
  onToggle,
  onSelectAll,
  onSelectNone,
  showEvents,
  showAvailability,
  showCalendarRequests,
  onToggleEvents,
  onToggleAvailability,
  onToggleCalendarRequests,
}) => {
  return (
    <div className="w-full max-h-48 md:max-h-none md:w-64 flex-shrink-0 bg-white border-b md:border-b-0 md:border-r border-gray-200 overflow-y-auto">
      <div className="p-4 space-y-4">
        {/* Visibility toggles */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Show
          </h3>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showEvents}
                onChange={onToggleEvents}
                className="rounded border-gray-300 text-blue-600"
              />
              Events
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showAvailability}
                onChange={onToggleAvailability}
                className="rounded border-gray-300 text-blue-600"
              />
              Availability / PTO
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showCalendarRequests}
                onChange={onToggleCalendarRequests}
                className="rounded border-gray-300 text-blue-600"
              />
              Calendar Requests
            </label>
          </div>
        </div>

        {/* Event type legend */}
        {showEvents && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Event Types
            </h3>
            <div className="space-y-1">
              {Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
                <div
                  key={type}
                  className="flex items-center gap-2 text-xs text-gray-600"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  {EVENT_TYPE_LABELS[type] ||
                    type
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (c) => c.toUpperCase())}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Availability type legend */}
        {showAvailability && (
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Availability Types
            </h3>
            <div className="space-y-1">
              {(
                Object.entries(AVAILABILITY_TYPE_COLORS) as [string, string][]
              ).map(([type, color]) => (
                <div
                  key={type}
                  className="flex items-center gap-2 text-xs text-gray-600"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  {AVAILABILITY_TYPE_LABELS[
                    type as keyof typeof AVAILABILITY_TYPE_LABELS
                  ] || type}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rep filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Reps ({selectedIds.size}/{positions.length})
            </h3>
            <div className="flex gap-1">
              <button
                onClick={onSelectAll}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                All
              </button>
              <span className="text-xs text-gray-400">|</span>
              <button
                onClick={onSelectNone}
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                None
              </button>
            </div>
          </div>

          <div className="space-y-1">
            {positions.map((pos, idx) => (
              <label
                key={pos.id}
                className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(pos.id)}
                  onChange={() => onToggle(pos.id)}
                  className="rounded border-gray-300"
                  style={{
                    accentColor: getRepColor(idx),
                  }}
                />
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getRepColor(idx) }}
                />
                <span className="truncate text-gray-700">
                  {pos.assigned_name || pos.title}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {POSITION_TYPE_LABELS[pos.position_type]?.charAt(0)}
                </span>
              </label>
            ))}
          </div>

          {positions.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No active sales positions
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RepFilterPanel;
